"use client";

import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import {
  AGGREGATED_ONBOARDING_CTA_CLASS,
  AGGREGATED_ONBOARDING_FOOTER_CLASS,
  AGGREGATED_ONBOARDING_SCROLL_CLASS,
  AGGREGATED_ONBOARDING_STEP_COLUMN_CLASS,
  AGGREGATED_ONBOARDING_STEP_ROOT_CLASS,
} from "../AggregatedFlow/aggregatedOnboardingLayoutTokens";
import { SaveGoalCardGoalPreview } from "@/src/components/Chat/InteractiveTool/SaveGoalCardGoalPreview";
import { TrackersSetupMeetEdHeader } from "./TrackersSetupMeetEdHeader";
import { TrackersSetupTrackerList } from "./TrackersSetupTrackerList";
import { TRACKERS_SETUP_MOCK } from "./trackersSetupMock";
import type { TrackersSetupOnboardingModalContentProps } from "./types";

/**
 * ⑧ Trackers 设置页。Meet Ed 固定顶栏；intro + 列表在中间滚动区；CTA 固定底栏。
 */
export const TrackersSetupOnboardingModalContent: React.FC<
  TrackersSetupOnboardingModalContentProps
> = ({ payload = TRACKERS_SETUP_MOCK, layout = "standalone", onClose }) => {
  const [isPc] = useIsPc();
  const isAggregated = layout === "aggregated";
  const {
    headerTitle,
    headline,
    subhead,
    saveGoalArgs,
    trackersSectionTitle,
    trackers,
    ctaLabelH5,
    ctaLabelPc,
  } = payload;
  const ctaLabel = isPc ? ctaLabelPc : ctaLabelH5;

  const contentColumnClass = cn(
    "flex flex-col px-4",
    isAggregated
      ? cn(AGGREGATED_ONBOARDING_SCROLL_CLASS, "md:px-6")
      : "min-h-0 flex-1 flex-col overflow-y-auto md:mx-auto md:w-full md:max-w-[600px] md:px-6",
  );

  const footerClass = isAggregated
    ? AGGREGATED_ONBOARDING_FOOTER_CLASS
    : "shrink-0 px-4 py-6 pb-safe md:mx-auto md:w-full md:max-w-[600px] md:px-6 md:py-6 md:pb-6";

  const ctaClass = cn(
    isAggregated
      ? cn(AGGREGATED_ONBOARDING_CTA_CLASS, "mb-3 md:mb-0")
      : "button button_m md_button primary_button flex h-11 w-full items-center justify-center rounded-2 md:w-auto md:min-w-[80px] md:px-4",
  );

  return (
    <div
      className={cn(
        isAggregated
          ? AGGREGATED_ONBOARDING_STEP_ROOT_CLASS
          : "flex h-full w-full flex-col bg-background_solid_secondary pt-safe",
      )}
      data-testid="trackers-setup-modal-view"
      data-layout={layout}
    >
      <div
        className={cn(
          isAggregated
            ? AGGREGATED_ONBOARDING_STEP_COLUMN_CLASS
            : "flex min-h-0 flex-1 flex-col md:h-auto md:flex-none md:gap-6",
        )}
        data-testid="trackers-setup-compare-root"
      >
        <header
          className={cn(
            "shrink-0 bg-background_solid_secondary px-4 pb-3 pt-2 md:px-6 md:pt-4",
            !isAggregated && "sticky top-0 z-10 md:static md:pb-0",
          )}
          data-testid="trackers-setup-meet-ed-header-block"
        >
          <TrackersSetupMeetEdHeader title={headerTitle} />
        </header>

        <div
          className={contentColumnClass}
          data-testid="trackers-setup-scroll-view"
        >
          <div
            className="flex flex-col gap-6"
            data-testid="trackers-setup-body-view"
          >
            <div
              className="flex flex-col gap-2"
              data-testid="trackers-setup-intro-block-view"
            >
              <p
                className="headline_regular_s text-content_primary"
                data-testid="trackers-setup-headline-view"
              >
                {headline}
              </p>
              <p
                className="body_output text-content_primary"
                data-testid="trackers-setup-subhead-view"
              >
                {subhead}
              </p>
              <SaveGoalCardGoalPreview args={saveGoalArgs} className="w-full" />
            </div>

            <p
              className="headline_regular_s text-content_primary"
              data-testid="trackers-setup-trackers-title-view"
            >
              {trackersSectionTitle}
            </p>

            <TrackersSetupTrackerList trackers={trackers} />
          </div>
        </div>

        <footer className={footerClass}>
          <button
            type="button"
            data-openreplay-label="TrackersSetup · Save this setup"
            data-testid="trackers-setup-cta-click"
            className={ctaClass}
            onClick={() => onClose("save")}
          >
            {ctaLabel}
          </button>
        </footer>
      </div>
    </div>
  );
};
